import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
    Box,
    Button,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Text,
} from '@razorpay/blade/components';
import { routePaths } from 'components/Routes/data';
import { BulkUploadTypes, UploadTypeConfig, UploadTypeConfigCallback } from 'constants/bulkUpload';
import permissions from 'constants/permissions';
import { format } from 'date-fns';
import { getRunPayrollMonthList } from 'utils/Dates';
import { toIndianCurrencyString } from 'utils/Numbers';
import { showToastViaEvent } from 'utils/ToastEvents';
import { AppError, getAppErrorDetails } from 'utils/AppError';
import api from 'api';
import pollForBulkDownload from 'utils/pollForBulkDownload';
import { BulkUploadStagingDetailsSchemaContract } from 'schemas/BulkUploadStagingDetailsSchema';

type StagingRow = BulkUploadStagingDetailsSchemaContract['bulk_uploads_staging']['data'][0];

/** Empty-state modal shown when the org has no adhoc earning components configured. */
const NoAdhocComponentsModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
    const history = useHistory();

    return (
        <Modal isOpen={isOpen} onDismiss={onClose}>
            <ModalHeader title="No adhoc earning components configured" />
            <ModalBody>
                <Text color="surface.text.gray.subtle">
                    No adhoc earning components are configured for your organisation yet.
                    Please set up at least one component before downloading the template.
                </Text>
            </ModalBody>
            <ModalFooter>
                <Box display="flex" gap="spacing.5" justifyContent="flex-end">
                    <Button variant="tertiary" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button onClick={() => history.push(routePaths.salaryComponents.earningsListView)}>
                        Go to Salary Components
                    </Button>
                </Box>
            </ModalFooter>
        </Modal>
    );
};

/**
 * Event dispatched (via window.postMessage) by the template-download handler below
 * when the backend reports the org has no adhoc earning components. The handler runs
 * outside React, so it cannot open the modal directly — ExtraFields bridges that gap.
 */
export const NO_ADHOC_COMPONENTS_EVENT = 'bulk-one-time-payment:no-adhoc-components';

/**
 * Rendered by the generic flow as `initiation.ExtraFields`. Owns the
 * NoAdhocComponentsModal and shows it when NO_ADHOC_COMPONENTS_EVENT is dispatched.
 */
const OneTimePaymentExtraFields = () => {
    const [showModal, setShowModal] = useState(false);

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            if (event.origin !== window.location.origin) {
                return;
            }
            if (event.data?.eventType === NO_ADHOC_COMPONENTS_EVENT) {
                setShowModal(true);
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, []);

    return <NoAdhocComponentsModal isOpen={showModal} onClose={() => setShowModal(false)} />;
};

const getCurrentMonthStr = () => {
    const today = new Date();
    today.setDate(1);
    today.setHours(0, 0, 0, 0);
    return format(today, 'yyyy-MM-dd');
};

const getPayrollMonthOptions = (selectedMonth: string) => {
    if (!selectedMonth) return [];
    const monthList = getRunPayrollMonthList(selectedMonth);
    return monthList.map((month) => ({
        value: format(month, 'yyyy-MM-dd'),
        label: format(month, 'MMMM yyyy'),
    }));
};

const getColumns = (): NonNullable<UploadTypeConfig['preview']['columns']> => [
    {
        title: 'Employee ID',
        render: (item: StagingRow) => item.file_row?.commonProperties?.employeeId ?? '-',
    },
    {
        title: 'Name',
        render: (item: StagingRow) => item.file_row?.commonProperties?.employeeName ?? '-',
    },
    {
        title: 'Email ID',
        render: (item: StagingRow) => item.file_row?.commonProperties?.email ?? '-',
    },
    {
        title: 'Adhoc Component',
        render: (item: StagingRow) => item.file_row?.adhocComponent ?? '-',
    },
    {
        title: 'Amount',
        render: (item: StagingRow) =>
            item.file_row?.amount
                ? toIndianCurrencyString(parseFloat(item.file_row.amount), true)
                : '-',
    },
    {
        title: 'TDS Recovery Option',
        render: (item: StagingRow) => item.file_row?.tdsRecoveryOption ?? '-',
    },
];

export const bulkOneTimePaymentConfig: UploadTypeConfigCallback = (data) => {
    const urlParams = new URLSearchParams(data.history.location.search);
    const payrollMonth = urlParams.get('payrollMonth') || '';

    return {
        permissions: [
            permissions.PAYROLL_ADD_ADHOC_PAYMENT,
            permissions.PAYROLL_PAY_ADHOC_PAYMENT,
        ],
        initiation: {
            feature: 'bulkOneTimePaymentFlag',
            pageTitle: 'Bulk One-Time Payments',
            step1Title: '1. Download the template',
            step1Subtitle:
                'Download the template for the selected payroll month, fill in the one-time payment details, then upload it.',
            instructions: [
                'All one-time payments will be applied to the payroll month selected above.',
                'ADD new rows if the same employee has multiple one-time payments.',
                'DELETE rows for employees with no one-time payments.',
                'Select the Adhoc Earning Component from the dropdown in column D.',
                'The maximum number of rows you can upload is 500.',
            ],
            hasMonthSelector: true,
            skipMonthChangeConfirmation: true,
            dropdownSelectorLabel: '🗓 Select payroll month',
            getDropdownOptions: getPayrollMonthOptions,
            defaultDropdownValue: getCurrentMonthStr(),
            ExtraFields: OneTimePaymentExtraFields,
            onDropdownOptionChange(selectedMonth) {
                if (selectedMonth) {
                    data.history.push({
                        pathname: routePaths.bulkUploads.initiate,
                        search: `?type=${BulkUploadTypes.TYPE_BULK_ONE_TIME_PAYMENT}&payrollMonth=${selectedMonth}`,
                    });
                }
            },
            async onSampleTemplateDownload(_orgId, _reportType) {
                if (!payrollMonth) return { isTemplateDownloaded: false };

                const tempDate = `${payrollMonth.substring(0, 4)}-${payrollMonth.substring(5, 7)}-01`;

                try {
                    const response = await api.bulkUploads.downloadOneTimePaymentTemplate(tempDate);

                    if (response.status === 422) {
                        const json = await response.json();
                        if (json.error === 'no_adhoc_components') {
                            // Config runs outside React — signal the ExtraFields modal to open.
                            window.postMessage(
                                { eventType: NO_ADHOC_COMPONENTS_EVENT },
                                window.location.origin,
                            );
                            return { isTemplateDownloaded: false };
                        }
                        throw new AppError({ messageForUser: json?.message ?? 'No eligible employees found for the selected payroll month.' });
                    }

                    if (!response.ok) {
                        const json = await response.json();
                        throw new AppError({ messageForUser: json?.message ?? 'Something went wrong while downloading the template. Please try again.' });
                    }

                    // Dual-shape response: JSON = large org (async, emailed → poll); otherwise a
                    // binary file streamed directly (small org). The check tells the two apart.
                    const contentType = response.headers.get('Content-Type') ?? '';

                    if (contentType.includes('application/json')) {
                        const json = await response.json();
                        pollForBulkDownload(json.bulk_download_id);
                        showToastViaEvent({
                            text: 'Your template is being prepared. You can leave this page — we will email you the download link once it is ready.',
                            timeout: 6000,
                            type: 'success',
                            icon: 'info',
                        });
                        return { isTemplateDownloaded: false };
                    }

                    const blob = await response.blob();
                    const disposition = response.headers.get('Content-Disposition') ?? '';
                    const fileNameMatch = disposition.match(/filename[^;=\n]*=(['"]?)([^'";\n]*)\1/);
                    const fileName = fileNameMatch ? fileNameMatch[2] : 'Bulk-One-Time-Payment-Template';

                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = fileName;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    URL.revokeObjectURL(url);
                    return { isTemplateDownloaded: true };
                } catch (error) {
                    const { messageForUser } = getAppErrorDetails(error);
                    showToastViaEvent({
                        text: messageForUser || 'Something went wrong while downloading the template. Please try again.',
                        timeout: 5000,
                        type: 'danger',
                        icon: 'info',
                    });
                    return { isTemplateDownloaded: false };
                }
            },
        },
        preview: {
            feature: 'bulkOneTimePaymentFlag',
            columns: getColumns(),
            renderConfirmationText: () =>
                `The one-time payments in this sheet will be applied to your ${payrollMonth ? format(new Date(payrollMonth), 'MMMM yyyy') : ''} payroll. Confirm to proceed.`,
            successModalCtaText: 'Go to Adhoc Payments',
            successModalMainText: 'One-time payments applied successfully.',
            successModalSubText: () =>
                'The payments will be reflected in the selected payroll month.',
            onSuccessModalCtaClick() {
                window.location.href = '/adhocPayment';
            },
            onDownloadErrorReport(history) {
                history?.push({
                    pathname: routePaths.bulkUploads.initiate,
                    search: `?type=${BulkUploadTypes.TYPE_BULK_ONE_TIME_PAYMENT}${payrollMonth ? `&payrollMonth=${payrollMonth}` : ''}`,
                });
            },
        },
    };
};
